import React, { useEffect, useRef, useState, useMemo } from 'react';
import {
    Box,
    Typography,
    Paper,
    Chip,
    Fade,
    alpha,
    useTheme,
} from '@mui/material';
import {
    CheckCircle as JeduIcon,
    Cancel as NejeduIcon,
    AccessTime as TimeIcon,
    HourglassEmpty as EmptyIcon,
} from '@mui/icons-material';
import { Odpoved } from '../api/JsdhNotifikaceService/generated';
import { formatDateTime } from '../utils/utils';

interface LiveTimelineProps {
    odpovedi: Odpoved[];
    casVyhlaseni?: string;
}

interface TimelineItem extends Odpoved {
    isNew: boolean;
    dobaReakce: number; // v sekundách
}

const LiveTimeline: React.FC<LiveTimelineProps> = ({
    odpovedi,
    casVyhlaseni,
}) => {
    const theme = useTheme();
    const [seenIds, setSeenIds] = useState<Set<number>>(new Set());
    const prevOdpovediRef = useRef<Odpoved[]>([]);

    // Detekce nových odpovědí a výpočet doby reakce
    const timelineItems = useMemo((): TimelineItem[] => {
        const casVyhlaseniTime = casVyhlaseni ? new Date(casVyhlaseni).getTime() : null;

        return odpovedi
            .map((o) => {
                const casOdpovedi = new Date(o.cas).getTime();
                const dobaReakce = casVyhlaseniTime
                    ? Math.floor((casOdpovedi - casVyhlaseniTime) / 1000)
                    : 0;

                return {
                    ...o,
                    isNew: !seenIds.has(o.id),
                    dobaReakce,
                };
            })
            .sort((a, b) => new Date(b.cas).getTime() - new Date(a.cas).getTime());
    }, [odpovedi, casVyhlaseni, seenIds]);

    // Označit nové položky jako viděné po animaci
    useEffect(() => {
        const newIds = odpovedi.map((o) => o.id);
        const hasNewItems = newIds.some((id) => !seenIds.has(id));

        if (hasNewItems) {
            const timer = setTimeout(() => {
                setSeenIds((prev) => {
                    const updated = new Set(prev);
                    newIds.forEach((id) => updated.add(id));
                    return updated;
                });
            }, 2000);

            return () => clearTimeout(timer);
        }
    }, [odpovedi, seenIds]);

    // Reset viděných ID při změně poplachu
    useEffect(() => {
        if (casVyhlaseni) {
            setSeenIds(new Set());
        }
    }, [casVyhlaseni]);

    // Uložení předchozích odpovědí
    useEffect(() => {
        prevOdpovediRef.current = odpovedi;
    }, [odpovedi]);

    const formatDobaReakce = (sekundy: number): string => {
        if (sekundy < 60) {
            return `${sekundy}s`;
        }
        const minuty = Math.floor(sekundy / 60);
        const zbytekSekund = sekundy % 60;
        return zbytekSekund > 0 ? `${minuty}m ${zbytekSekund}s` : `${minuty}m`;
    };

    const pocetJedu = odpovedi.filter((o) => o.odpoved === 'JEDU').length;
    const pocetNejedu = odpovedi.filter((o) => o.odpoved === 'NEJEDU').length;

    const getDobaReakceColor = (sekundy: number) => {
        if (sekundy < 120) return theme.palette.success.main;
        if (sekundy < 300) return theme.palette.warning.main;
        return theme.palette.text.secondary;
    };

    return (
        <Paper
            elevation={0}
            sx={{
                p: 2.5,
                borderRadius: 2,
                bgcolor: 'background.paper',
                height: '100%',
            }}
        >
            {/* Header */}
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2.5}>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    Odpovědi v reálném čase
                </Typography>
                <Box display="flex" gap={1}>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.75,
                            px: 1.5,
                            py: 0.5,
                            borderRadius: 2,
                            bgcolor: alpha(theme.palette.success.main, 0.1),
                            border: `1px solid ${alpha(theme.palette.success.main, 0.3)}`,
                        }}
                    >
                        <JeduIcon sx={{ fontSize: 18, color: 'success.main' }} />
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'success.main' }}>
                            {pocetJedu}
                        </Typography>
                    </Box>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.75,
                            px: 1.5,
                            py: 0.5,
                            borderRadius: 2,
                            bgcolor: alpha(theme.palette.error.main, 0.1),
                            border: `1px solid ${alpha(theme.palette.error.main, 0.3)}`,
                        }}
                    >
                        <NejeduIcon sx={{ fontSize: 18, color: 'error.main' }} />
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'error.main' }}>
                            {pocetNejedu}
                        </Typography>
                    </Box>
                </Box>
            </Box>

            {/* Timeline */}
            {timelineItems.length === 0 ? (
                <Box
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                    justifyContent="center"
                    minHeight={150}
                    gap={1}
                >
                    <EmptyIcon sx={{ fontSize: 40, color: 'text.disabled' }} />
                    <Typography variant="body2" color="text.secondary">
                        Zatím žádné odpovědi
                    </Typography>
                </Box>
            ) : (
                <Box
                    sx={{
                        maxHeight: 400,
                        overflowY: 'auto',
                        pr: 0.5,
                    }}
                >
                <Box display="flex" flexDirection="column" gap={1}>
                    {timelineItems.map((item) => {
                        const jeJedu = item.odpoved === 'JEDU';
                        const statusColor = jeJedu
                            ? theme.palette.success.main
                            : theme.palette.error.main;

                        return (
                            <Fade
                                key={item.id}
                                in={true}
                                timeout={item.isNew ? 1000 : 0}
                            >
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1.5,
                                        p: 1.25,
                                        borderRadius: 1.5,
                                        borderLeft: `4px solid ${statusColor}`,
                                        bgcolor: item.isNew
                                            ? alpha(statusColor, 0.12)
                                            : alpha(statusColor, 0.04),
                                        transition: 'background-color 2s ease-out',
                                        '&:hover': {
                                            bgcolor: alpha(statusColor, 0.08),
                                        },
                                    }}
                                >
                                    {/* Icon */}
                                    {jeJedu ? (
                                        <JeduIcon sx={{ fontSize: 24, color: 'success.main', flexShrink: 0 }} />
                                    ) : (
                                        <NejeduIcon sx={{ fontSize: 24, color: 'error.main', flexShrink: 0 }} />
                                    )}

                                    {/* Content */}
                                    <Box flex={1} minWidth={0}>
                                        <Box display="flex" alignItems="center" gap={0.75} flexWrap="wrap">
                                            <Typography
                                                variant="body2"
                                                sx={{ fontWeight: 600 }}
                                            >
                                                {item.jmeno}
                                            </Typography>
                                            {item.funkce?.map((f) => (
                                                <Chip
                                                    key={f.kod}
                                                    label={f.kod}
                                                    size="small"
                                                    sx={{
                                                        height: 20,
                                                        fontSize: '0.7rem',
                                                        fontWeight: 600,
                                                        bgcolor: alpha(theme.palette.text.primary, 0.08),
                                                    }}
                                                />
                                            ))}
                                        </Box>
                                        <Box
                                            display="flex"
                                            alignItems="center"
                                            gap={0.5}
                                            mt={0.25}
                                        >
                                            <TimeIcon sx={{ fontSize: 13, color: 'text.disabled' }} />
                                            <Typography variant="caption" color="text.secondary">
                                                {formatDateTime(item.cas)}
                                            </Typography>
                                            {item.dobaReakce > 0 && (
                                                <Typography
                                                    variant="caption"
                                                    sx={{
                                                        ml: 0.5,
                                                        fontWeight: 600,
                                                        color: getDobaReakceColor(item.dobaReakce),
                                                    }}
                                                >
                                                    {formatDobaReakce(item.dobaReakce)}
                                                </Typography>
                                            )}
                                        </Box>
                                    </Box>

                                    {/* Status badge */}
                                    <Chip
                                        label={jeJedu ? 'JEDU' : 'NEJEDU'}
                                        size="small"
                                        sx={{
                                            flexShrink: 0,
                                            fontWeight: 700,
                                            fontSize: '0.7rem',
                                            color: '#fff',
                                            bgcolor: statusColor,
                                        }}
                                    />
                                </Box>
                            </Fade>
                        );
                    })}
                </Box>
                </Box>
            )}
        </Paper>
    );
};

export default React.memo(LiveTimeline);
